"use client";

import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLocaleFromPathname, useLocalePath } from "@/hooks/useLocalePath";
import { pathMatchesEnglishRoute } from "@/utils/routes";
import { getDemoPageKey, withDemoSource } from "@/utils/demoSource";
import { useTrackDemoCtaClick } from "@/hooks/useDemoCtaTracking";
import Logo from "../Logo";
import MenuIcon from "../icons/MenuIcon";
import Button from "../library/Button";
import DemoButtonText from "./DemoButtonText";
import { RedesignButton } from "@/components/redesign/RedesignButton";
import { useDemoButtonLink } from "@/hooks/useDemoButtonText";
import { useRoutedGailAppUrl } from "@/providers/AppLinkRoutingProvider";
import { type MarketingHeaderVariant } from "@/constants/redesign";
import { MarketingHeaderDesktop } from "@/components/redesign/MarketingHeaderDesktop";
import { useLenis } from "lenis/react";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/scrollLock";

// Simple CloseIcon component
const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Custom NavLink component
type NavLinkProps = {
  href: string;
  active?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
};

const NavLink: React.FC<NavLinkProps> = ({
  href,
  active,
  children,
  onClick,
  className,
}) => {
  return (
    <Link
      href={href}
      className={clsx(
        "transition-colors",
        "hover:text-gail-primary",
        active ? "text-gail-primary" : "text-gray-800",
        className,
      )}
      onClick={onClick}
    >
      {children}
    </Link>
  );
};

// Desktop Navigation component
type DesktopNavigationProps = {
  pathname: string | null;
};

const DesktopNavigation: React.FC<DesktopNavigationProps> = ({ pathname }) => {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const lang = getLocaleFromPathname(pathname);
  const demoLink = useDemoButtonLink(lp("/contact-sales"));
  const dashboardHref = useRoutedGailAppUrl(
    process.env.NEXT_PUBLIC_GAIL_DASHBOARD_URL ?? "#",
  );

  return (
    <div className="flex items-center">
      <div className="flex space-x-8 mr-6">
        <NavLink
          href={lp("/product")}
          active={pathMatchesEnglishRoute(pathname, lang, "/product")}
        >
          {t("header.product")}
        </NavLink>
        <NavLink
          href={lp("/pricing")}
          active={pathMatchesEnglishRoute(pathname, lang, "/pricing")}
        >
          {t("header.pricing")}
        </NavLink>
        <NavLink
          href={lp("/compliance")}
          active={pathMatchesEnglishRoute(pathname, lang, "/compliance")}
        >
          {t("header.compliance")}
        </NavLink>
        <NavLink href={dashboardHref}>{t("header.signIn")}</NavLink>
      </div>
      <Button href={demoLink.href} target={demoLink.target} className="text-sm">
        <DemoButtonText fallback={t("header.requestDemo")} />
      </Button>
    </div>
  );
};

type DefaultHeaderDesktopProps = {
  pathname: string | null;
};

const DefaultHeaderDesktop: React.FC<DefaultHeaderDesktopProps> = ({
  pathname,
}) => {
  const { t } = useTranslation();
  const lp = useLocalePath();

  return (
    <div className="hidden md:flex w-full items-center justify-between">
      <Link
        href={lp("/")}
        className="shrink-0"
        aria-label={t("breadcrumb.home")}
      >
        <Logo />
      </Link>
      <DesktopNavigation pathname={pathname} />
    </div>
  );
};

// Mobile Menu component
type MobileMenuProps = {
  isOpen: boolean;
  toggleMenu: () => void;
  pathname: string | null;
  isRedesign?: boolean;
};

const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  toggleMenu,
  pathname,
  isRedesign = false,
}) => {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const lang = getLocaleFromPathname(pathname);
  const demoLink = useDemoButtonLink(lp("/contact-sales"));
  const trackDemoCtaClick = useTrackDemoCtaClick();
  const mobileDemoSource = `nav_mobile_${getDemoPageKey(pathname)}`;
  const dashboardHref = useRoutedGailAppUrl(
    process.env.NEXT_PUBLIC_GAIL_DASHBOARD_URL ?? "#",
  );

  return (
    <div
      className={clsx(
        "md:hidden",
        "transition-all duration-300",
        "w-full h-dvh",
        "fixed inset-0",
        isRedesign ? "bg-marketing-surface" : "bg-[#F9F3E6]",
        "z-50",
        "flex flex-col",
        "px-2 sm:px-4",
        isOpen
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none",
      )}
    >
      <div className="flex justify-between px-2 py-4 pt-[17px]">
        <Link
          href={lp("/")}
          className="shrink-0"
          aria-label={t("breadcrumb.home")}
          onClick={() => toggleMenu()}
        >
          <Logo />
        </Link>
        <button
          onClick={toggleMenu}
          className="text-black transition-all duration-300"
          aria-label="Close menu"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="flex flex-col justify-between px-2 h-full min-h-0">
        <div className="flex flex-col font-semibold text-[40px]">
          <NavLink
            href={lp("/product")}
            active={pathMatchesEnglishRoute(pathname, lang, "/product")}
            onClick={toggleMenu}
            className="border-black/20 border-b"
          >
            {t("header.product")}
          </NavLink>
          <NavLink
            href={lp("/pricing")}
            active={pathMatchesEnglishRoute(pathname, lang, "/pricing")}
            onClick={toggleMenu}
            className="border-black/20 border-b"
          >
            {t("header.pricing")}
          </NavLink>
          <NavLink
            href={lp("/compliance")}
            active={pathMatchesEnglishRoute(pathname, lang, "/compliance")}
            onClick={toggleMenu}
            className="border-black/20 border-b"
          >
            {t("header.compliance")}
          </NavLink>
          <NavLink href={dashboardHref} onClick={toggleMenu}>
            {t("header.signIn")}
          </NavLink>
        </div>

        <div className="relative flex-1 my-4 min-h-0 overflow-hidden">
          <Image
            src="/images/mobile_nav.webp"
            alt="Gail Menu Graphic"
            fill
            sizes="100vw"
            className="object-contain"
          />
        </div>

        <div className="flex flex-col gap-2 mb-8">
          {!isRedesign && (
            <Button
              type="tertiary"
              href={lp("/signup")}
              className="py-3 border border-neutral-400 w-full font-normal text-base"
            >
              {t("header.signUp")}
            </Button>
          )}
          {isRedesign ? (
              <RedesignButton
                href={withDemoSource(lp("/contact-sales"), mobileDemoSource)}
                onClick={() =>
                  trackDemoCtaClick({
                    source: mobileDemoSource,
                    placement: "nav_mobile",
                  })
                }
                className="w-full py-3"
              >
                {t("redesign.hero.ctaLabel")}
              </RedesignButton>
          ) : (
            <Button
              type="primary"
              href={demoLink.href}
              target={demoLink.target}
              className="py-3 border border-black w-full font-normal text-base"
            >
              <DemoButtonText fallback={t("header.requestDemo")} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Header component
type HeaderProps = {
  variant?: MarketingHeaderVariant;
};

const Header: React.FC<HeaderProps> = ({ variant = "default" }) => {
  const { t } = useTranslation();
  const isRedesign = variant === "redesign";
  const [show, setShow] = useState(true);
  const [isAtTop, setIsAtTop] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const lastScroll = useRef(0);
  const ticking = useRef(false);
  const pathname = usePathname();
  const lp = useLocalePath();
  const lenis = useLenis();

  const isMainPage =
    pathname === "/" || /^\/[a-z]{2}(\/)?$/.test(pathname ?? "");

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Prevent scrolling while the mobile menu is open. The shared ref-counted
  // lock keeps other overlays (e.g. dialogs) from being unlocked by the menu.
  useEffect(() => {
    if (!isOpen) return;
    lockBodyScroll(lenis);
    return () => unlockBodyScroll(lenis);
  }, [isOpen, lenis]);

  useEffect(() => {
    const threshold = 16;
    const onScroll = () => {
      if (!ticking.current) {
        globalThis.requestAnimationFrame(() => {
          const y = window.scrollY;
          setIsAtTop(y < threshold);
          if (y < threshold) {
            setShow(true);
            lastScroll.current = y;
            ticking.current = false;
            return;
          }
          if (Math.abs(y - lastScroll.current) < threshold) {
            ticking.current = false;
            return;
          }
          if (y < lastScroll.current) setShow(true);
          else if (y > lastScroll.current) setShow(false);
          lastScroll.current = y;
          ticking.current = false;
        });
        ticking.current = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={clsx(
        "sticky",
        "top-0",
        "z-40",
        "w-full",
        !isRedesign &&
          (isMainPage && isAtTop ? "bg-white/30 backdrop-blur-md" : "bg-white"),
        isRedesign && [
          "border-b backdrop-blur-md",
          "transition-[background-color,border-color] duration-300 ease-in-out",
          isAtTop
            ? "bg-marketing-surface border-transparent"
            : "bg-marketing-surface/80 border-black/5",
        ],
        "font-monument",
        "px-4",
        // Redesign nav stays pinned so the demo CTA is always visible
        !isOpen &&
          !isRedesign && [
            "transition-all",
            "duration-300",
            "will-change-transform",
            show ? "translate-y-0" : "-translate-y-full",
          ],
      )}
    >
      <nav
        className={clsx(
          "mx-auto z-60 py-4",
          isRedesign ? "max-w-6xl" : "max-w-8xl md:px-0",
        )}
      >
        <div className="flex items-center justify-between md:hidden">
          <Link
            href={lp("/")}
            className="shrink-0"
            aria-label={t("breadcrumb.home")}
          >
            <Logo className={isRedesign ? "h-[22px]" : undefined} />
          </Link>
          <button
            onClick={toggleMenu}
            className="text-black transition-all duration-300"
            aria-label="Toggle menu"
          >
            <MenuIcon />
          </button>
        </div>

        {isRedesign ? (
          <MarketingHeaderDesktop pathname={pathname} />
        ) : (
          <DefaultHeaderDesktop pathname={pathname} />
        )}

        <MobileMenu
          isOpen={isOpen}
          toggleMenu={toggleMenu}
          pathname={pathname}
          isRedesign={isRedesign}
        />
      </nav>
    </header>
  );
};

export default Header;
